/**
 * `CertificationMedal` — rende l'immagine di una certificazione come una
 * "medaglietta" 3D interattiva, mantenendo la forma originale del badge.
 *
 * Interazioni:
 *  - al passaggio del mouse la medaglietta ruota leggermente attorno all'asse
 *    verticale (un "accenno" di giro) per suggerire che può essere girata;
 *  - trascinando con il mouse (o il dito) ruota su se stessa attorno all'asse
 *    verticale (rotazione orizzontale), mostrando il retro; al rilascio la
 *    rotazione prosegue per inerzia e decelera dolcemente;
 *  - un riflesso a banda diagonale (lo stesso trattamento "lucido" dello Score
 *    10+) scorre sulla superficie in funzione della rotazione.
 *
 * Fronte e retro condividono la sagoma dell'immagine: il fronte è lucido con
 * l'immagine del badge, il retro è opaco e riporta nome, ente e date "incise".
 *
 * Nota sulle performance: l'angolo di rotazione NON è tenuto in stato React. Il
 * trascinamento e l'inerzia scrivono la `transform` direttamente sul nodo (in un
 * ciclo `requestAnimationFrame`), evitando un re-render per frame. La transizione
 * CSS è attiva solo quando la medaglietta è a riposo (accenno all'hover): se
 * restasse attiva durante l'inerzia, ogni frame la farebbe ripartire producendo
 * un movimento a scatti.
 *
 * Rispetta `prefers-reduced-motion`: con il flag attivo l'accenno di rotazione
 * all'hover e l'inerzia sono disattivati (resta possibile il flip via drag).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { CERTIFICATION_PLACEHOLDER_IMAGE } from './CertificationCard';

/** Coppia etichetta/valore "incisa" sul retro della medaglietta. */
export interface MedalBackField {
  /** Etichetta breve (es. "Conseguita"), mostrata in maiuscoletto. */
  label: string;
  /** Valore (es. una data formattata o un conteggio). */
  value: string;
}

interface CertificationMedalProps {
  /**
   * URL dell'immagine del badge (fronte). Se assente si usa il placeholder
   * condiviso delle certificazioni (`certification-placeholder.png`).
   */
  imageUrl?: string;
  /** Nome della certificazione (usato sul retro e per l'accessibilità). */
  name: string;
  /** Ente emittente, mostrato inciso sul retro (facoltativo). */
  issuer?: string;
  /**
   * Campi incisi sul retro sotto il nome/ente (es. date, conteggi). Ogni pagina
   * decide cosa mostrare; se vuoto, il retro riporta solo nome ed ente. I campi
   * sono affiancati, quindi conviene tenerne pochi (1–3).
   */
  backFields?: readonly MedalBackField[];
  /** Classe aggiuntiva sul contenitore, per adattare la dimensione per pagina. */
  className?: string;
}

/** Gradi di rotazione Y per pixel trascinato orizzontalmente. */
const DRAG_SENSITIVITY = 0.5;

/**
 * Accenno di rotazione MASSIMO (gradi) al passaggio del mouse. L'angolo effettivo
 * è proporzionale alla distanza orizzontale del puntatore dal centro: al centro
 * ~0°, ai bordi ±`HOVER_HINT_MAX_DEG` (verso destra/sinistra), così l'inclinazione
 * segue il mouse e suggerisce la direzione in cui la medaglietta può girare.
 */
const HOVER_HINT_MAX_DEG = 26;

/** Transizione usata quando la medaglietta è a riposo (accenno all'hover). */
const IDLE_TRANSITION = 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Corsa massima del riflesso, in percentuale della larghezza della banda. Va
 * tenuta contenuta: con valori alti la banda esce dalla sagoma del badge e la
 * luce sembra "spegnersi" invece di scorrere sulla superficie.
 */
const SHINE_TRAVEL = 38;

/** Scostamento laterale massimo dell'ombra (px), a metà rotazione. */
const SHADOW_MAX_SHIFT = 28;

/** Scostamento verticale costante dell'ombra (px): luce appena dall'alto. */
const SHADOW_OFFSET_Y = 14;

/** Sfocatura di base dell'ombra (px), a faccia dritta. */
const SHADOW_BASE_BLUR = 20;

/**
 * Opacità massima dell'ombreggiatura sulla superficie, raggiunta quando la
 * medaglietta è di taglio (metà della superficie completamente sfuggente).
 */
const SHADE_MAX_OPACITY = 0.62;

/**
 * Attrito dell'inerzia espresso come frazione di velocità che sopravvive dopo
 * 16.67 ms (un frame a 60 fps). Il decadimento effettivo è normalizzato sul
 * tempo trascorso, così il rallentamento è identico a 60, 120 o 144 Hz.
 */
const INERTIA_FRICTION_PER_FRAME = 0.94;

/** Sotto questa velocità (deg/ms) l'inerzia si considera esaurita. */
const INERTIA_MIN_SPEED = 0.004;

/** Velocità massima dell'inerzia (deg/ms), per evitare giri incontrollati. */
const MAX_SPEED = 2.5;

/** Peso del campione corrente nella media esponenziale della velocità. */
const VELOCITY_SMOOTHING = 0.25;

/** Transizione dello snap: leggermente più lunga per un aggancio morbido. */
const SNAP_TRANSITION = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';

/** Oltre questa pausa (ms) dall'ultimo movimento il lancio è considerato fermo. */
const STALE_MOVE_MS = 90;

/**
 * Soglia (0..255) oltre la quale un canale è considerato "chiaro". Un pixel è
 * bucato se R, G e B superano tutti la soglia (bianco/quasi bianco).
 */
const WHITE_KEY_THRESHOLD = 232;

/** Alpha minima perché un pixel sia considerato parte del badge (silhouette). */
const SILHOUETTE_MIN_ALPHA = 24;

/** Immagini derivate dal badge, ottenute con una sola passata di canvas. */
interface DerivedBadgeImages {
  /**
   * Badge con i pixel bianchi resi trasparenti: usato come immagine del fronte,
   * così la texture brushed sottostante traspare dai fori.
   */
  keyed: string;
  /**
   * Silhouette piena del badge (tutti i pixel non trasparenti resi opachi):
   * usata come maschera per la forma. Non deve avere i fori del chroma-key,
   * altrimenti la piastra del retro risulterebbe bucata e si vedrebbe
   * attraverso.
   */
  silhouette: string;
}

/**
 * Carica il badge e ne ricava le due immagini derivate (vedi
 * `DerivedBadgeImages`). Entrambe sono data URL, quindi same-origin: importante
 * perché `mask-image` da origini diverse può essere ignorata dal browser.
 *
 * Ritorna `null` finché non è pronto o se non è possibile leggere i pixel
 * (canvas "tainted" per mancanza di header CORS): in quel caso il chiamante
 * ricade sull'immagine originale.
 */
function useDerivedBadgeImages(imageUrl: string | undefined): DerivedBadgeImages | null {
  const [derived, setDerived] = useState<DerivedBadgeImages | null>(null);

  useEffect(() => {
    setDerived(null);
    if (imageUrl === undefined || typeof document === 'undefined') return;

    let cancelled = false;
    const img = new Image();
    // Necessario per poter leggere i pixel da un'immagine cross-origin.
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (cancelled) return;
      try {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx === null) return;
        ctx.drawImage(img, 0, 0);

        const source = ctx.getImageData(0, 0, width, height);
        const px = source.data;
        // Buffer separato per la silhouette: nero pieno dove il badge esiste.
        const shape = ctx.createImageData(width, height);
        const shapePx = shape.data;

        for (let i = 0; i < px.length; i += 4) {
          const alpha = px[i + 3];
          if (alpha >= SILHOUETTE_MIN_ALPHA) {
            shapePx[i + 3] = 255; // opaco: fa parte della sagoma
          }
          if (
            px[i] >= WHITE_KEY_THRESHOLD &&
            px[i + 1] >= WHITE_KEY_THRESHOLD &&
            px[i + 2] >= WHITE_KEY_THRESHOLD
          ) {
            px[i + 3] = 0; // pixel bianco → foro trasparente sul fronte
          }
        }

        ctx.putImageData(source, 0, 0);
        const keyed = canvas.toDataURL('image/png');

        ctx.clearRect(0, 0, width, height);
        ctx.putImageData(shape, 0, 0);
        const silhouette = canvas.toDataURL('image/png');

        if (!cancelled) setDerived({ keyed, silhouette });
      } catch {
        // Canvas tainted o lettura non riuscita: si resta sull'originale.
        if (!cancelled) setDerived(null);
      }
    };
    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return derived;
}

/** Rileva la preferenza utente per la riduzione del movimento. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function CertificationMedal({
  imageUrl: imageUrlProp,
  name,
  issuer,
  backFields,
  className,
}: CertificationMedalProps) {
  const reducedMotion = usePrefersReducedMotion();

  // Quando la certificazione non ha un'immagine propria si usa il placeholder
  // condiviso (`certification-placeholder.png`): così fronte, maschere e retro
  // seguono la stessa pipeline del badge, senza ricadere sull'iniziale.
  const imageUrl = imageUrlProp ?? CERTIFICATION_PLACEHOLDER_IMAGE;

  // Derivate del badge: immagine con il bianco bucato (fronte) e silhouette
  // piena (maschere). Finché non sono pronte — o se l'immagine non è leggibile
  // per CORS — si usa l'originale come fallback.
  const derived = useDerivedBadgeImages(imageUrl);
  const frontSrc = derived?.keyed ?? imageUrl;
  const hasBrushedFront = derived !== null;

  const cardRef = useRef<HTMLDivElement>(null);
  const shineRef = useRef<HTMLSpanElement>(null);
  /** Banda del riflesso sul retro (metallo brushed). */
  const backShineRef = useRef<HTMLSpanElement>(null);
  /**
   * Strati di ombreggiatura da luce frontale: due per faccia, uno per lato.
   * Tenerli separati evita di dover specchiare un unico strato (vedi `paint`).
   */
  const frontShadeRightRef = useRef<HTMLSpanElement>(null);
  const frontShadeLeftRef = useRef<HTMLSpanElement>(null);
  const backShadeRightRef = useRef<HTMLSpanElement>(null);
  const backShadeLeftRef = useRef<HTMLSpanElement>(null);
  /** Elementi che portano il `drop-shadow`: immagine del fronte e piastra retro. */
  const frontVisualRef = useRef<HTMLElement | null>(null);
  const backPlateRef = useRef<HTMLSpanElement | null>(null);
  /** Callback ref per accettare indifferentemente `img` o `span` sul fronte. */
  const setFrontVisual = useCallback((node: HTMLElement | null) => {
    frontVisualRef.current = node;
  }, []);

  // Solo per il cursore: l'angolo vive nei ref, non in stato.
  const [dragging, setDragging] = useState(false);

  /** Rotazione accumulata attorno all'asse Y (gradi). */
  const spin = useRef(0);
  /** Offset dell'accenno all'hover (gradi). */
  const hint = useRef(0);
  /** Velocità corrente in deg/ms, smussata sui campioni di `pointermove`. */
  const velocity = useRef(0);
  /** Timestamp dell'ultimo `pointermove` utile, per scartare lanci "fermi". */
  const lastMoveAt = useRef(0);
  /** Coordinata X e timestamp del campione precedente durante il drag. */
  const lastSample = useRef({ x: 0, t: 0 });
  /** Handle del ciclo d'inerzia in corso (`null` quando non attivo). */
  const inertiaRaf = useRef<number | null>(null);

  /** Scrive rotazione e posizione del riflesso direttamente sul DOM. */
  const paint = useCallback(() => {
    const angle = spin.current + hint.current;
    if (cardRef.current !== null) {
      cardRef.current.style.transform = `rotateY(${angle.toFixed(2)}deg)`;
    }
    const rad = (angle * Math.PI) / 180;
    const sin = Math.sin(rad);

    // Il riflesso scorre in funzione della rotazione: la banda di luce "spazza"
    // la superficie man mano che la medaglietta gira.
    const shineShift = sin * SHINE_TRAVEL;
    if (shineRef.current !== null) {
      shineRef.current.style.transform = `translateX(${shineShift.toFixed(1)}%)`;
    }
    if (backShineRef.current !== null) {
      // Retro controruotato di 180°: si specchia lo scorrimento perché la luce
      // spazzi nello stesso verso a schermo.
      backShineRef.current.style.transform = `translateX(${(-shineShift).toFixed(1)}%)`;
    }

    // Ombreggiatura con luce frontale: la luce arriva dall'osservatore, quindi
    // la metà di superficie che ruota allontanandosi riceve meno luce e si
    // scurisce. A faccia dritta (sin = 0) la luce colpisce tutto in modo
    // uniforme e l'ombreggiatura scompare; di taglio (|sin| = 1) è massima.
    //
    // Quale lato si allontana: con `rotateY` positivo il bordo destro va verso
    // il fondo, quindi l'ombra sta a destra (in coordinate schermo).
    //
    // Ogni faccia ha DUE strati, uno per lato, e si modula solo la loro opacità:
    // quello del lato che si avvicina resta a zero. Un unico strato specchiato
    // con `scaleX(±1)` non va bene, perché il verso è una grandezza discreta che
    // cambia di colpo a sin = 0 (faccia dritta): dentro una transizione CSS
    // `scaleX` interpola attraverso `scaleX(0)`, quindi l'ombra si comprimeva al
    // centro e riapriva specchiata, spazzando visibilmente tutta la medaglietta;
    // senza transizione, invece, salterebbe di lato mentre è ancora visibile.
    // Con due strati la grandezza continua è solo l'opacità, che a sin = 0 vale
    // zero per entrambi: il passaggio da un lato all'altro è quindi impercettibile
    // e si anima in modo pulito.
    //
    // Fronte e retro condividono le stesse opacità: il contenitore del retro
    // (`.cert-medal__shade--back`) è specchiato con `scaleX(-1)` per allineare la
    // maschera alla sagoma del rovescio, e quel ribaltamento annulla quello della
    // faccia controruotata di 180°. In coordinate schermo i due strati "destri"
    // coprono quindi entrambi il lato destro.
    const shadeRight = (Math.max(0, sin) * SHADE_MAX_OPACITY).toFixed(3);
    const shadeLeft = (Math.max(0, -sin) * SHADE_MAX_OPACITY).toFixed(3);
    for (const shade of [frontShadeRightRef.current, backShadeRightRef.current]) {
      if (shade !== null) shade.style.opacity = shadeRight;
    }
    for (const shade of [frontShadeLeftRef.current, backShadeLeftRef.current]) {
      if (shade !== null) shade.style.opacity = shadeLeft;
    }

    // Ombra classica dietro il badge (`drop-shadow`, che segue la sagoma), con
    // i parametri animati in base alla rotazione. Con luce frontale l'ombra sta
    // dietro l'oggetto: a faccia dritta è centrata sotto il badge, mentre
    // ruotando sbuca di lato e si sfoca, perché la piastra si inclina rispetto
    // alla sorgente. Lo scostamento è massimo a metà rotazione e torna a zero su
    // entrambe le facce, così l'ombra è presente sia sul fronte sia sul retro.
    const offsetX = sin * SHADOW_MAX_SHIFT;
    const blur = SHADOW_BASE_BLUR + Math.abs(sin) * 12;
    const alpha = 0.3 - Math.abs(sin) * 0.08;
    const makeShadow = (dx: number, alphaBoost = 0) =>
      `drop-shadow(${dx.toFixed(1)}px ${SHADOW_OFFSET_Y}px ${blur.toFixed(1)}px rgba(0, 0, 0, ${(alpha + alphaBoost).toFixed(3)}))`;

    if (frontVisualRef.current !== null) {
      frontVisualRef.current.style.filter = makeShadow(offsetX);
    }
    if (backPlateRef.current !== null) {
      // Il retro è controruotato di 180°: nelle sue coordinate locali l'asse X è
      // invertito rispetto allo schermo. Senza specchiare l'offset, l'ombra del
      // retro cadrebbe dal lato opposto a quella del fronte (luce incoerente) e
      // finirebbe nascosta sotto la piastra. Così invece resta visibile e dalla
      // stessa parte anche mentre si guarda il retro.
      //
      // L'ombra è anche un po' più marcata: la piastra è chiara su fondo pagina
      // chiaro, quindi con la stessa intensità del fronte si leggerebbe meno.
      backPlateRef.current.style.filter = makeShadow(-offsetX, 0.08);
    }
  }, []);

  /**
   * Applica una transizione CSS a card e riflesso (o `'none'`). Devono
   * condividere durata e curva: se la banda di luce si spostasse istantaneamente
   * mentre la card ruota in transizione, il riflesso "salterebbe".
   */
  const applyTransition = useCallback((value: string) => {
    if (cardRef.current !== null) cardRef.current.style.transition = value;
    if (shineRef.current !== null) shineRef.current.style.transition = value;
    if (backShineRef.current !== null) backShineRef.current.style.transition = value;
    // Ombreggiature e ombra (su `filter`) devono seguire la stessa tempistica:
    // con `none` nessuna transizione, altrimenti si replica durata/curva.
    const timing = value === 'none' ? null : value.replace('transform', '').trim();
    // Solo `opacity`: il verso degli strati d'ombreggiatura è fisso e non va mai
    // animato (vedi `paint`).
    for (const shade of [
      frontShadeRightRef.current,
      frontShadeLeftRef.current,
      backShadeRightRef.current,
      backShadeLeftRef.current,
    ]) {
      if (shade === null) continue;
      shade.style.transition = timing === null ? 'none' : `opacity ${timing}`;
    }
    for (const el of [frontVisualRef.current, backPlateRef.current]) {
      if (el === null) continue;
      el.style.transition = timing === null ? 'none' : `filter ${timing}`;
    }
  }, []);

  /** Abilita/disabilita la transizione "a riposo" (accenno all'hover). */
  const setTransition = useCallback(
    (enabled: boolean) => applyTransition(enabled ? IDLE_TRANSITION : 'none'),
    [applyTransition],
  );

  /**
   * Aggancia la rotazione al multiplo di 180° più vicino, così la medaglietta
   * si ferma dritta sulla faccia che si sta guardando. Usa una transizione
   * dedicata (più lunga dell'accenno hover) per un arrivo morbido.
   */
  const snapToNearestFace = useCallback(() => {
    spin.current = Math.round(spin.current / 180) * 180;
    // Con reduced-motion l'aggancio è istantaneo (nessuna animazione).
    applyTransition(reducedMotion ? 'none' : SNAP_TRANSITION);
    paint();
  }, [applyTransition, paint, reducedMotion]);

  const stopInertia = useCallback(() => {
    if (inertiaRaf.current !== null) {
      cancelAnimationFrame(inertiaRaf.current);
      inertiaRaf.current = null;
    }
  }, []);

  // Posizione iniziale (e ripristino dopo un eventuale re-render del padre).
  // Dipende anche da `derived`: quando le immagini derivate arrivano, il
  // proiettore d'ombra del fronte sostituisce l'immagine come portatore del
  // `drop-shadow`, quindi va ridipinto sul nuovo elemento.
  useEffect(() => {
    setTransition(true);
    paint();
  }, [derived, paint, setTransition]);

  useEffect(() => stopInertia, [stopInertia]);

  /**
   * Ciclo d'inerzia: integra la velocità sul tempo reale trascorso e la fa
   * decadere in modo indipendente dal refresh rate. La transizione CSS resta
   * disattivata per tutta la durata, così ogni frame è un salto esatto e il
   * movimento risulta continuo.
   */
  const startInertia = useCallback(() => {
    stopInertia();
    setTransition(false);
    let previous = performance.now();
    const step = (now: number) => {
      // Clamp del delta: dopo un frame perso (o tab in background) un dt enorme
      // produrrebbe un salto brusco.
      const dt = Math.min(now - previous, 50);
      previous = now;

      spin.current += velocity.current * dt;
      velocity.current *= Math.pow(INERTIA_FRICTION_PER_FRAME, dt / 16.67);
      paint();

      if (Math.abs(velocity.current) < INERTIA_MIN_SPEED) {
        velocity.current = 0;
        inertiaRaf.current = null;
        // Fine corsa: aggancia la faccia più vicina in posizione dritta.
        snapToNearestFace();
        return;
      }
      inertiaRaf.current = requestAnimationFrame(step);
    };
    inertiaRaf.current = requestAnimationFrame(step);
  }, [paint, setTransition, stopInertia]);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      stopInertia();
      setTransition(false);
      velocity.current = 0;
      // Il drag parte dall'angolo attuale: si incorpora l'accenno d'hover in
      // `spin` e lo si azzera, così la medaglietta non "torna al centro" prima
      // di iniziare a ruotare.
      spin.current += hint.current;
      hint.current = 0;
      lastSample.current = { x: e.clientX, t: performance.now() };
      lastMoveAt.current = lastSample.current.t;
      setDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      paint();
    },
    [paint, setTransition, stopInertia],
  );

  /**
   * Aggiorna l'accenno d'hover in base alla posizione orizzontale del puntatore:
   * frazione firmata `[-1, 1]` della semi-larghezza (negativa a sinistra del
   * centro, positiva a destra) × l'angolo massimo. Usa una transizione morbida
   * così il movimento del mouse non risulta a scatti.
   */
  const updateHoverHint = useCallback(
    (target: HTMLElement, clientX: number, animate: boolean) => {
      const rect = target.getBoundingClientRect();
      const half = rect.width / 2;
      if (half <= 0) return;
      const fraction = Math.max(-1, Math.min(1, (clientX - (rect.left + half)) / half));
      hint.current = fraction * HOVER_HINT_MAX_DEG;
      // Al primo ingresso si anima (dal centro alla posizione del mouse); nei
      // movimenti successivi la transizione è disattivata così l'inclinazione
      // segue il puntatore in modo diretto e fluido, come durante il drag. Con
      // la transizione attiva, ogni move la farebbe ripartire → scatti.
      setTransition(animate);
      paint();
    },
    [paint, setTransition],
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Fuori dal drag: inclinazione d'hover proporzionale alla posizione del
      // mouse (solo a riposo, per non interferire con inerzia/snap).
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
        if (!reducedMotion && !dragging && inertiaRaf.current === null) {
          updateHoverHint(e.currentTarget, e.clientX, false);
        }
        return;
      }

      const now = performance.now();
      const dx = e.clientX - lastSample.current.x;
      const dt = now - lastSample.current.t;
      lastSample.current = { x: e.clientX, t: now };

      spin.current += dx * DRAG_SENSITIVITY;
      paint();

      // Velocità istantanea smussata con media esponenziale: un singolo campione
      // è troppo rumoroso e produrrebbe un'inerzia irregolare.
      if (dt > 0) {
        const instant = (dx * DRAG_SENSITIVITY) / dt; // deg/ms
        velocity.current =
          velocity.current * (1 - VELOCITY_SMOOTHING) + instant * VELOCITY_SMOOTHING;
        if (dx !== 0) lastMoveAt.current = now;
      }
    },
    [dragging, paint, reducedMotion, updateHoverHint],
  );

  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const wasDragging = e.currentTarget.hasPointerCapture(e.pointerId);
      if (wasDragging) e.currentTarget.releasePointerCapture(e.pointerId);
      setDragging(false);
      if (!wasDragging) return;

      // Se il puntatore era fermo poco prima del rilascio non c'è "lancio":
      // la medaglietta si ferma dov'è, senza inerzia residua spuria.
      const idleTooLong = performance.now() - lastMoveAt.current > STALE_MOVE_MS;
      const speed = Math.abs(velocity.current);
      if (reducedMotion || idleTooLong || speed < INERTIA_MIN_SPEED * 4) {
        // Nessun lancio: aggancia subito la faccia più vicina in posizione dritta.
        velocity.current = 0;
        snapToNearestFace();
        return;
      }

      velocity.current = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, velocity.current));
      startInertia();
    },
    [reducedMotion, snapToNearestFace, startInertia],
  );

  const handlePointerEnter = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // L'accenno si applica solo a riposo: durante drag o inerzia sarebbe un
      // salto indesiderato in mezzo al movimento.
      if (reducedMotion || dragging || inertiaRaf.current !== null) return;
      // Primo ingresso: anima dolcemente dal centro fino alla posizione del mouse.
      updateHoverHint(e.currentTarget, e.clientX, true);
    },
    [dragging, reducedMotion, updateHoverHint],
  );

  const handlePointerLeave = useCallback(() => {
    if (hint.current === 0) return;
    hint.current = 0;
    if (inertiaRaf.current === null) setTransition(true);
    paint();
  }, [paint, setTransition]);

  // Maschera con la sagoma del badge: usata sia per confinare il riflesso sia
  // per dare al retro la stessa forma dell'immagine (niente cerchio).
  //
  // Si usa la SILHOUETTE (non l'immagine keyed): la keyed ha i fori al posto del
  // bianco e, usata come maschera, bucherebbe la piastra del retro rendendola
  // trasparente. La silhouette è un data URL, quindi same-origin: importante
  // perché `mask-image` da origini diverse può essere ignorata dal browser,
  // facendo ricadere il retro sul fallback di forma.
  const maskSource = derived?.silhouette ?? imageUrl;
  const maskStyle = useMemo<CSSProperties>(() => {
    if (maskSource === undefined) return {};
    const url = `url("${maskSource}")`;
    return {
      maskImage: url,
      WebkitMaskImage: url,
      maskSize: 'contain',
      WebkitMaskSize: 'contain',
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      maskPosition: 'center',
      WebkitMaskPosition: 'center',
    } as CSSProperties;
  }, [maskSource]);

  const initial = name.charAt(0).toUpperCase();

  return (
    <div className={`cert-medal${className !== undefined ? ` ${className}` : ''}`}>
      <div
        className={`cert-medal__scene${dragging ? ' cert-medal__scene--dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        role="img"
        aria-label={`Medaglia della certificazione ${name}. Trascina per ruotarla.`}
      >

        <div ref={cardRef} className="cert-medal__card">
          {/* Fronte: lucido, segue la sagoma dell'immagine del badge. */}
          <div className="cert-medal__face cert-medal__face--front">
            {/* Texture brushed dietro l'immagine: traspare dai fori bianchi del
                badge (chroma-key). Il riflesso lucido vive QUI dentro, così
                appare solo sul metallo (i fori) e non sulla grafica a colori:
                l'immagine opaca sopra lo copre nelle zone stampate. */}
            {hasBrushedFront && (
              <>
                {/* Proiettore d'ombra del fronte: porta il `drop-shadow` su una
                    SILHOUETTE PIENA del badge. Non si può usare l'immagine come
                    proiettore, perché il chroma-key le ha tolto i pixel bianchi:
                    su un badge quadrato con bordo/sfondo bianco il perimetro
                    sparisce e con esso l'ombra esterna. Resta invisibile perché
                    coperto dal layer brushed e dall'immagine. */}
                <span
                  ref={setFrontVisual}
                  className="cert-medal__front-shadow"
                  aria-hidden="true"
                >
                  <span
                    className="cert-medal__front-shadow-fill"
                    style={maskStyle}
                  />
                </span>
                <span
                  className="cert-medal__front-brushed"
                  style={maskStyle}
                  aria-hidden="true"
                >
                  <span ref={shineRef} className="cert-medal__brushed-shine" />
                </span>
              </>
            )}
            {frontSrc !== undefined ? (
              <img
                // Con il proiettore attivo l'ombra non va sull'immagine (keyed).
                ref={hasBrushedFront ? undefined : setFrontVisual}
                src={frontSrc}
                alt=""
                aria-hidden="true"
                className="cert-medal__image"
                draggable={false}
              />
            ) : (
              <span
                ref={hasBrushedFront ? undefined : setFrontVisual}
                className="cert-medal__initial"
                aria-hidden="true"
              >
                {initial}
              </span>
            )}
            {/* Fallback senza brushed (immagine non leggibile per CORS): il
                riflesso resta sull'intera sagoma come prima. */}
            {!hasBrushedFront && (
              <span className="cert-medal__shine" style={maskStyle} aria-hidden="true">
                <span ref={shineRef} className="cert-medal__shine-band" />
              </span>
            )}
            {/* Ombreggiatura da luce frontale sul lato che si allontana: uno
                strato per lato, si accende solo quello che serve. */}
            <span className="cert-medal__shade" style={maskStyle} aria-hidden="true">
              <span ref={frontShadeRightRef} className="cert-medal__shade-layer" />
              <span
                ref={frontShadeLeftRef}
                className="cert-medal__shade-layer cert-medal__shade-layer--left"
              />
            </span>
          </div>

          {/* Retro: opaco, con la stessa sagoma dell'immagine; nome e date
              "incise". La maschera dà la forma; l'incisione resta piena. */}
          <div className="cert-medal__face cert-medal__face--back" aria-hidden="true">
            {/* Involucro con il solo `drop-shadow` e figlio con la sola maschera:
                nel rendering CSS la maschera è applicata DOPO il filtro, quindi
                tenendoli sullo stesso elemento l'ombra verrebbe ritagliata via
                dalla sagoma. Separandoli, l'ombra è calcolata sul risultato già
                mascherato e resta visibile fuori dal contorno. */}
            <span ref={backPlateRef} className="cert-medal__back-plate">
              <span
                className={`cert-medal__back-plate-fill${maskSource === undefined ? ' cert-medal__back-plate-fill--fallback' : ''}`}
                style={maskStyle}
              >
                {/* Riflesso lucido sul metallo del retro: figlio diretto
                    dell'elemento mascherato, senza wrapper con `overflow`, così
                    l'unico ritaglio è la sagoma del badge e la banda copre tutta
                    la superficie. */}
                <span ref={backShineRef} className="cert-medal__brushed-shine" />
              </span>
            </span>
            <div className="cert-medal__engraving">
              <span className="cert-medal__engraved-name">{name}</span>
              {issuer !== undefined && (
                <span className="cert-medal__engraved-issuer">{issuer}</span>
              )}
              {backFields !== undefined && backFields.length > 0 && (
                <>
                  <span className="cert-medal__engraved-rule" />
                  <dl className="cert-medal__engraved-dates">
                    {backFields.map((field) => (
                      <div key={field.label}>
                        <dt>{field.label}</dt>
                        <dd>{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
            </div>
            {/* Stessa ombreggiatura del fronte: anche il retro reagisce alla
                luce frontale mentre ruota. Il modificatore `--back` specchia la
                maschera così il contorno combacia con quello della piastra. */}
            <span
              className="cert-medal__shade cert-medal__shade--back"
              style={maskStyle}
              aria-hidden="true"
            >
              <span ref={backShadeRightRef} className="cert-medal__shade-layer" />
              <span
                ref={backShadeLeftRef}
                className="cert-medal__shade-layer cert-medal__shade-layer--left"
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CertificationMedal;
